import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { PayrollService } from "services/payroll";

const AFP_CODES = ["capital", "cuprum", "habitat", "planvital", "provida", "modelo", "uno"];
const fmtCLP = (n) => "$" + Math.round(n || 0).toLocaleString("es-CL");

function ContractForm({ initial = {}, employees, onChange }) {
  const [form, setForm] = useState({
    employee: initial.employee?._id || initial.employee || "",
    type: initial.type || "indefinido",
    startDate: initial.startDate ? initial.startDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    endDate: initial.endDate ? initial.endDate.slice(0, 10) : "",
    baseSalary: initial.baseSalary || 800000,
    gratificationType: initial.gratificationType || "legal",
    colacion: initial.colacion || 0,
    movilizacion: initial.movilizacion || 0,
    afpCode: initial.afpCode || "modelo",
    afpFund: initial.afpFund || "c",
    healthPlan: initial.healthPlan || "fonasa",
    isapreAmount: initial.isapreAmount || "",
    familyAllowanceLoads: initial.familyAllowanceLoads || 0,
    apvAmount: initial.apvAmount || 0,
    status: initial.status || "ACTIVE",
  });
  useEffect(() => { onChange(form); }, [form]);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, pt: 1 }}>
      <Select size="small" value={form.employee} onChange={(e) => setForm({ ...form, employee: e.target.value })} displayEmpty sx={{ gridColumn: "1 / 3" }}>
        <MenuItem value="" disabled>Selecciona empleado…</MenuItem>
        {employees.map((emp) => (
          <MenuItem key={emp._id} value={emp._id}>{emp.firstName} {emp.lastName} — {emp.rut}</MenuItem>
        ))}
      </Select>
      <Select size="small" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
        <MenuItem value="indefinido">Indefinido</MenuItem>
        <MenuItem value="plazo_fijo">Plazo fijo</MenuItem>
        <MenuItem value="por_obra">Por obra</MenuItem>
      </Select>
      <Select size="small" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
        <MenuItem value="ACTIVE">Vigente</MenuItem>
        <MenuItem value="ENDED">Terminado</MenuItem>
      </Select>
      <TextField label="Fecha inicio" type="date" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} size="small" />
      <TextField label="Fecha término (opcional)" type="date" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} size="small" />
      <TextField label="Sueldo base (CLP)" type="number" value={form.baseSalary} onChange={(e) => setForm({ ...form, baseSalary: Number(e.target.value) })} size="small" />
      <Select size="small" value={form.gratificationType} onChange={(e) => setForm({ ...form, gratificationType: e.target.value })}>
        <MenuItem value="legal">Gratificación legal</MenuItem>
        <MenuItem value="convenida">Convenida</MenuItem>
        <MenuItem value="none">Sin gratificación</MenuItem>
      </Select>
      <TextField label="Colación" type="number" value={form.colacion} onChange={(e) => setForm({ ...form, colacion: Number(e.target.value) })} size="small" />
      <TextField label="Movilización" type="number" value={form.movilizacion} onChange={(e) => setForm({ ...form, movilizacion: Number(e.target.value) })} size="small" />
      <Select size="small" value={form.afpCode} onChange={(e) => setForm({ ...form, afpCode: e.target.value })}>
        {AFP_CODES.map((c) => <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>)}
      </Select>
      <Select size="small" value={form.afpFund} onChange={(e) => setForm({ ...form, afpFund: e.target.value })}>
        {["a", "b", "c", "d", "e"].map((f) => <MenuItem key={f} value={f}>Fondo {f.toUpperCase()}</MenuItem>)}
      </Select>
      <Select size="small" value={form.healthPlan} onChange={(e) => setForm({ ...form, healthPlan: e.target.value })}>
        <MenuItem value="fonasa">Fonasa</MenuItem>
        <MenuItem value="isapre">Isapre</MenuItem>
      </Select>
      {form.healthPlan === "isapre" && (
        <TextField label="Plan Isapre (UF)" type="number" value={form.isapreAmount} onChange={(e) => setForm({ ...form, isapreAmount: Number(e.target.value) })} size="small" />
      )}
      <TextField label="Cargas familiares" type="number" value={form.familyAllowanceLoads} onChange={(e) => setForm({ ...form, familyAllowanceLoads: Number(e.target.value) })} size="small" />
      <TextField label="APV (CLP)" type="number" value={form.apvAmount} onChange={(e) => setForm({ ...form, apvAmount: Number(e.target.value) })} size="small" />
    </Box>
  );
}

export function ContractsPanel({ memberId }) {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [resC, resE] = await Promise.all([
        PayrollService.listContracts(memberId),
        PayrollService.listEmployees(memberId, { status: "ACTIVE" }),
      ]);
      setItems(resC.items || []);
      setEmployees(resE.items || []);
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);

  const handleSubmit = async () => {
    if (!form.employee) { setFeedback({ kind: "error", msg: "Selecciona un empleado" }); return; }
    try {
      const payload = { ...form };
      if (!payload.endDate) delete payload.endDate;
      if (editing) {
        await PayrollService.updateContract(memberId, editing._id, payload);
      } else {
        await PayrollService.createContract(memberId, payload);
      }
      setFeedback({ kind: "ok", msg: editing ? "Contrato actualizado" : "Contrato creado" });
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("¿Eliminar contrato?")) return;
    try { await PayrollService.deleteContract(memberId, id); await load(); }
    catch (e) { setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message }); }
  };

  return (
    <Box>
      {feedback && <Alert severity={feedback.kind === "ok" ? "success" : "error"} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.msg}</Alert>}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setForm({}); setModalOpen(true); }}>
          Nuevo contrato
        </Button>
      </Box>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Empleado</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Inicio</TableCell>
                <TableCell>Término</TableCell>
                <TableCell align="right">Sueldo base</TableCell>
                <TableCell>AFP</TableCell>
                <TableCell>Salud</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin contratos</TableCell></TableRow>
              ) : items.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell>{c.employee?.firstName} {c.employee?.lastName}</TableCell>
                  <TableCell><Chip size="small" label={c.type} /></TableCell>
                  <TableCell>{c.startDate ? new Date(c.startDate).toLocaleDateString("es-CL") : "—"}</TableCell>
                  <TableCell>{c.endDate ? new Date(c.endDate).toLocaleDateString("es-CL") : "—"}</TableCell>
                  <TableCell align="right">{fmtCLP(c.baseSalary)}</TableCell>
                  <TableCell>{c.afpCode}</TableCell>
                  <TableCell>{c.healthPlan}</TableCell>
                  <TableCell><Chip size="small" label={c.status} color={c.status === "ACTIVE" ? "success" : "default"} /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => { setEditing(c); setModalOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleRemove(c._id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} fullWidth maxWidth="md">
        <DialogTitle>{editing ? "Editar contrato" : "Nuevo contrato"}</DialogTitle>
        <DialogContent>
          <ContractForm initial={editing || {}} employees={employees} onChange={setForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setModalOpen(false); setEditing(null); }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}>{editing ? "Guardar" : "Crear"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
